import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Users, ShieldCheck, CalendarDays, IndianRupee, Percent, Eye, MousePointerClick, RefreshCw, Activity } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

const rupees = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

export default function AnalyticsPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/analytics");
      setData(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load analytics");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (loading || !data) return <div className="text-sm text-slate-500">Loading analytics…</div>;

  const maxCity = Math.max(1, ...data.shifts.by_city.map(c => c.count));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-[0.2em] text-slate-500">Overview</div>
        <Button variant="outline" size="sm" onClick={load} className="rounded-full" data-testid="analytics-refresh"><RefreshCw className="w-4 h-4 mr-1.5" /> Refresh</Button>
      </div>

      {/* Users */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi testid="kpi-users-total" icon={<Users className="w-4 h-4" />} label="Total users" value={data.users.total} sub={`${data.users.professionals} pros · ${data.users.facilities} facilities`} />
        <Kpi testid="kpi-verified" icon={<ShieldCheck className="w-4 h-4" />} label="Verified pros" value={data.users.verified_professionals} sub={`${data.users.pending_professionals} pending review`} accent="emerald" />
        <Kpi testid="kpi-shifts" icon={<CalendarDays className="w-4 h-4" />} label="Shifts posted" value={data.shifts.total} sub={`${data.shifts.by_status.open} open · ${data.shifts.by_status.confirmed} confirmed · ${data.shifts.by_status.completed} done`} />
        <Kpi testid="kpi-cancel-rate" icon={<Percent className="w-4 h-4" />} label="Cancel rate" value={`${data.cancel_rate_pct}%`} sub={`${data.shifts.by_status.cancelled} cancelled / ${data.shifts.by_status.completed} completed`} accent={data.cancel_rate_pct > 20 ? "rose" : "slate"} />
      </div>

      {/* GMV */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi testid="kpi-gmv" icon={<IndianRupee className="w-4 h-4" />} label="Total GMV" value={rupees(data.payments.gmv_total_inr)} sub="Across all payment states" accent="emerald" />
        <Kpi testid="kpi-gmv-released" icon={<IndianRupee className="w-4 h-4" />} label="Released" value={rupees(data.payments.by_status_inr.released || 0)} sub={`Commission earned: ${rupees(data.payments.commission_released_inr)}`} />
        <Kpi testid="kpi-gmv-held" icon={<IndianRupee className="w-4 h-4" />} label="In escrow (held)" value={rupees(data.payments.by_status_inr.held || 0)} sub="Awaiting shift completion" />
        <Kpi testid="kpi-gmv-refunded" icon={<IndianRupee className="w-4 h-4" />} label="Refunded" value={rupees(data.payments.by_status_inr.refunded || 0)} sub="From cancellations" accent="rose" />
      </div>

      {/* Shifts by city */}
      <Card className="rounded-2xl border-slate-200 p-5 bg-white">
        <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-4">Shifts by city</div>
        {data.shifts.by_city.length === 0 ? (
          <div className="text-sm text-slate-500">No shifts posted yet.</div>
        ) : (
          <div className="space-y-2.5">
            {data.shifts.by_city.map(c => (
              <div key={c.city} className="flex items-center gap-3">
                <div className="w-24 text-sm text-slate-700 font-medium">{c.city}</div>
                <div className="flex-1 h-6 rounded-lg bg-slate-100 overflow-hidden">
                  <div className="h-full bg-emerald-600" style={{ width: `${(c.count / maxCity) * 100}%` }} />
                </div>
                <div className="w-10 text-right text-sm text-slate-700 tabular-nums">{c.count}</div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Traffic */}
      <div className="grid lg:grid-cols-2 gap-4">
        <Card className="rounded-2xl border-slate-200 p-5 bg-white">
          <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-4">Signup funnel · last 30 days</div>
          <FunnelRow icon={<Eye className="w-3.5 h-3.5" />} label="Landing views" value={data.traffic.funnel_30d.landing_view} base={data.traffic.funnel_30d.landing_view} />
          <FunnelRow icon={<MousePointerClick className="w-3.5 h-3.5" />} label="Sign-in CTA clicks" value={data.traffic.funnel_30d.cta_click} base={data.traffic.funnel_30d.landing_view} />
          <FunnelRow icon={<ShieldCheck className="w-3.5 h-3.5" />} label="Onboarding completed" value={data.traffic.funnel_30d.onboarding_completed} base={data.traffic.funnel_30d.landing_view} />
        </Card>

        <Card className="rounded-2xl border-slate-200 p-5 bg-white">
          <div className="flex items-center justify-between mb-4">
            <div className="text-xs uppercase tracking-[0.2em] text-slate-500 flex items-center gap-1.5"><Activity className="w-3.5 h-3.5" /> Traffic</div>
            <div className="text-[10px] uppercase tracking-[0.15em] text-slate-400">Last 7 days</div>
          </div>
          <div className="grid grid-cols-3 gap-3 mb-4">
            <MiniStat label="Events · 24h" value={data.traffic.events_last_24h} />
            <MiniStat label="Events · 7d" value={data.traffic.events_last_7d} />
            <MiniStat label="Sessions · 24h" value={data.traffic.unique_sessions_24h} />
          </div>
          <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500 mb-2">Top events</div>
          {data.traffic.top_events_7d.length === 0 ? (
            <div className="text-sm text-slate-500">No events yet.</div>
          ) : (
            <div className="space-y-1.5">
              {data.traffic.top_events_7d.map(e => (
                <div key={e.event} className="flex items-center justify-between text-sm">
                  <span className="text-slate-700 truncate mr-2 font-mono text-xs">{e.event}</span>
                  <span className="text-slate-500 tabular-nums text-xs">{e.count}</span>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      <div className="text-[11px] text-slate-400 text-right">Generated: {new Date(data.generated_at).toLocaleString("en-IN")}</div>
    </div>
  );
}

function Kpi({ icon, label, value, sub, accent = "slate", testid }) {
  const cls = {
    slate: "text-slate-700",
    emerald: "text-emerald-800",
    rose: "text-rose-700",
  }[accent] || "text-slate-700";
  return (
    <Card data-testid={testid} className="rounded-2xl border-slate-200 p-5 bg-white">
      <div className={"text-xs uppercase tracking-[0.2em] text-slate-500 flex items-center gap-1.5"}>
        <span className={cls}>{icon}</span> {label}
      </div>
      <div className={"font-display text-3xl font-extrabold mt-2 " + cls}>{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
    </Card>
  );
}

function FunnelRow({ icon, label, value, base }) {
  const pct = base > 0 ? Math.round((value / base) * 100) : 0;
  return (
    <div className="mb-2 last:mb-0">
      <div className="flex items-center justify-between text-sm mb-1">
        <span className="inline-flex items-center gap-1.5 text-slate-700">{icon} {label}</span>
        <span className="tabular-nums text-slate-700"><b>{value}</b> <span className="text-slate-400 text-xs">({pct}%)</span></span>
      </div>
      <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
        <div className="h-full bg-emerald-600" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function MiniStat({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500">{label}</div>
      <div className="font-display text-2xl font-bold mt-0.5 tabular-nums">{value}</div>
    </div>
  );
}
